import React, { useState, useRef, useEffect } from 'react'
import { X, ChevronDown, Check } from 'lucide-react'

export interface MultiSelectOption {
  id: number
  name: string
  email?: string
}

interface MultiSelectProps {
  options: MultiSelectOption[]
  selectedIds: number[]
  onChange: (selectedIds: number[]) => void
  placeholder?: string
  className?: string
  disabled?: boolean
}

const MultiSelect: React.FC<MultiSelectProps> = ({
  options,
  selectedIds,
  onChange,
  placeholder = 'Select...',
  className = '',
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
        setSearchTerm('')
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const selectedOptions = options.filter(opt => selectedIds.includes(opt.id))
  
  const filteredOptions = options.filter(opt =>
    opt.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (opt.email && opt.email.toLowerCase().includes(searchTerm.toLowerCase()))
  )

  const toggleOption = (optionId: number) => {
    if (selectedIds.includes(optionId)) {
      onChange(selectedIds.filter(id => id !== optionId))
    } else {
      onChange([...selectedIds, optionId])
    }
  }

  const removeOption = (optionId: number, e: React.MouseEvent) => {
    e.stopPropagation()
    onChange(selectedIds.filter(id => id !== optionId))
  }

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Main Input */}
      <div
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={`min-h-[42px] px-3 py-2 border border-input rounded-lg bg-background flex items-center gap-2 flex-wrap cursor-pointer ${
          disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-ring'
        } ${isOpen ? 'border-ring ring-2 ring-ring/20' : ''}`}
      >
        {/* Selected Items */}
        {selectedOptions.length > 0 ? (
          <div className="flex items-center gap-2 flex-wrap flex-1">
            {selectedOptions.map(option => (
              <div
                key={option.id}
                className="flex items-center gap-1 px-2 py-1 bg-accent text-accent-foreground rounded text-sm"
              >
                <span>{option.name}</span>
                <button
                  onClick={(e) => removeOption(option.id, e)}
                  className="hover:bg-muted rounded p-0.5"
                  disabled={disabled}
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <span className="text-muted-foreground flex-1">{placeholder}</span>
        )}

        {/* Dropdown Arrow */}
        <ChevronDown
          className={`w-5 h-5 text-muted-foreground transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </div>

      {/* Dropdown */}
      {isOpen && !disabled && (
        <div className="absolute z-50 w-full mt-1 bg-card border border-border rounded-lg shadow-lg max-h-64 overflow-hidden flex flex-col">
          {/* Search Input */}
          <div className="p-2 border-b border-border">
            <input
              ref={inputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search..."
              className="w-full px-3 py-2 border border-input rounded bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              autoFocus
            />
          </div>

          {/* Options List */}
          <div className="overflow-y-auto flex-1">
            {filteredOptions.length > 0 ? (
              filteredOptions.map(option => {
                const isSelected = selectedIds.includes(option.id)
                return (
                  <div
                    key={option.id}
                    onClick={() => toggleOption(option.id)}
                    className={`flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors ${
                      isSelected
                        ? 'bg-accent'
                        : 'hover:bg-accent'
                    }`}
                  >
                    {/* Checkbox */}
                    <div
                      className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                        isSelected
                          ? 'bg-primary border-primary'
                          : 'border-input'
                      }`}
                    >
                      {isSelected && <Check className="w-3 h-3 text-primary-foreground" />}
                    </div>

                    {/* Option Info */}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-foreground">
                        {option.name}
                      </div>
                      {option.email && (
                        <div className="text-xs text-muted-foreground truncate">
                          {option.email}
                        </div>
                      )}
                    </div>
                  </div>
                )
              })
            ) : (
              <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                No options found
              </div>
            )}
          </div>

          {/* Footer */}
          {selectedOptions.length > 0 && (
            <div className="p-2 border-t border-border flex items-center justify-between">
              <span className="text-xs text-muted-foreground">
                {selectedOptions.length} selected
              </span>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  onChange([])
                }}
                className="text-xs text-primary hover:text-primary/80"
              >
                Clear all
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default MultiSelect

